import { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { useFetchDocuments } from "~/hooks/api/document/useFetchDocuments";
import { useFetchStudyInfo } from "~/hooks/api/study/useFetchStudyInfo";
import { LoadingContext, actionTypes } from "~/context/LoadingContext";
import DescriptionIcon from "~/content/icons/description.svg?react";
import InfoCircleIcon from "~/content/icons/info-circle.svg?react";

import { ArrowBadge } from "../../shared/ArrowBadge/ArrowBadge";
import { DashboardTile } from "../../shared/DashboardTile/DashboardTile";

import "./DocumentsTile.css";

export function DocumentsTile({ setFetchError }) {
  const { dispatch } = useContext(LoadingContext);

  const { studyInfo } = useFetchStudyInfo();
  const getInfo = (key) => studyInfo?.[key];

  const { t } = useTranslation();

  const { binderSummary, error, isLoading } = useFetchDocuments(
    () => {
      setFetchError("documents.documents", false);
    },
    () => {
      setFetchError("documents.documents", true);
    }
  );

  useEffect(() => {
    const LOADING_KEY = "documents";
    const type = isLoading
      ? actionTypes.ADD_LOADING_ITEM
      : actionTypes.REMOVE_LOADING_ITEM;

    dispatch({
      type,
      key: LOADING_KEY,
    });
  }, [dispatch, isLoading]);

  if (!binderSummary || binderSummary.bindersCount <= 0) {
    return null;
  }

  const needsAttention =
    !error && binderSummary.totalBinderNeedTakenAction > 0;

  return (
    <DashboardTile
      className="documents-tile"
      controls={() =>
        !error ? (
          <ArrowBadge color={needsAttention ? "orange" : "grey"} />
        ) : null
      }
      disabled={!!error}
      hasError={!!error}
      page={`${import.meta.env.VITE_VIEDOC_SHARE_BASE_URL}/${getInfo("studyGuid")}`}
      testId="documents-tile"
      isExternalUrl={true}
    >
      <div className="dashboard-tile-column">
        <h2 className="dashboard-tile-column__title">
          {t("documents.documents")}
        </h2>
        <div className="dashboard-tile-column__bottom">
          {needsAttention ? (
            <InfoCircleIcon
              className="documents-tile__attention-icon"
              aria-hidden="true"
            />
          ) : (
            <DescriptionIcon
              className="dashboard-tile-column__icon"
              aria-hidden="true"
            />
          )}
          <span className="dashboard-tile-column__description">
            {needsAttention
              ? t("documents.attention", {
                  count: binderSummary.totalBinderNeedTakenAction,
                })
              : t("documents.count", { count: binderSummary.bindersCount })}
          </span>
        </div>
      </div>
    </DashboardTile>
  );
}

DocumentsTile.propTypes = {
  setFetchError: PropTypes.func.isRequired,
};
